import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getAllTickets } from '../services/TicketDeCaisseService';
import { useTranslation } from 'react-i18next';

import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';
import TICKET_TYPE, { normalizeTicketType, getTicketTypeLabel, getTicketTypeColor } from '../../../constants/ticketTypes';

const TicketDeCaissePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Get store info from Redux store
  const storeInfo = useSelector((state) => state.company?.companyInfo || state.auth?.companyInfo);
  
  // Get payment methods from Redux
  const paymentMethodsFromStore = useSelector((state) => state.paymentMethod.paymentMethods) || [];

  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchTickets();
    // Fetch payment methods if not loaded
    if (paymentMethodsFromStore.length === 0) {
      dispatch(fetchPaymentMethods());
    }
  }, []);

  useEffect(() => {
    filterTickets();
  }, [searchTerm, tickets]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getAllTickets();
      
      // Handle different response structures
      let ticketsData = [];
      if (response?.payload && Array.isArray(response.payload)) {
        ticketsData = response.payload;
      } else if (response?.data?.payload && Array.isArray(response.data.payload)) {
        ticketsData = response.data.payload;
      } else if (Array.isArray(response?.data)) {
        ticketsData = response.data;
      } else if (Array.isArray(response)) {
        ticketsData = response;
      }
      
      setTickets(ticketsData);
      setFilteredTickets(ticketsData);
    } catch (error) {
      console.error('❌ Error fetching tickets:', error);
      setError(error.message || 'Failed to load tickets');
      toast.error(t('failed_to_load_tickets') || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const filterTickets = () => {
    if (!searchTerm.trim()) {
      setFilteredTickets(tickets);
      return;
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = tickets.filter((ticket) => {
      return (
        ticket.TicketNumber?.toLowerCase().includes(searchLower) ||
        ticket.CustomerName?.toLowerCase().includes(searchLower) ||
        ticket.PaymentMethodName?.toLowerCase().includes(searchLower) ||
        ticket.PaymentMethod?.toLowerCase().includes(searchLower)
      );
    });
    
    setFilteredTickets(filtered);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateTicket = () => {
    navigate('/tickets/add');
  };

  const handleViewTicket = (ticket) => {
    try {
      
      // Navigate to detail page
      navigate(`/tickets/view/${ticket.Id}`);
    } catch (error) {
      console.error('❌ Error viewing ticket:', error);
      toast.error(t('failed_to_load_ticket') || 'Failed to load ticket details');
    }
  };

  const handleEditTicket = (ticketId) => {
    navigate(`/tickets/edit/${ticketId}`);
  };

  const handlePrintTicket = async (ticket) => {
    try {
      
      // ✅ Fetch full ticket detail so SplitPayments / Payments are populated
      let fullTicket = ticket;
      try {
        const detail = await getTicketById(ticket.Id);
        if (detail) fullTicket = detail;
      } catch (e) {
        console.warn('⚠️ Could not fetch ticket detail, using list data:', e);
      }

      generateReceiptPreview(fullTicket);
    } catch (error) {
      console.error('❌ Error printing ticket:', error);
      toast.error(t('failed_to_print_ticket') || 'Failed to print ticket');
    }
  };

  const generateReceiptPreview = (ticket) => {

    // Prepare cart data from ticket line items - map to match POS format
    const cart = (ticket.LineItems || ticket.Items || []).map(item => ({
      Id: item.ProductId || item.Id,
      Name: item.ProductName || item.Name || 'Product',
      Price: parseFloat(item.UnitPriceExclTax || item.UnitPrice || item.Price || 0), // Base price (excl VAT)
      Quantity: parseFloat(item.Quantity || 1),
      VAT: parseFloat(item.VATRate || item.VAT || ticket.VATPercentage || 21),
      PriceInclVAT: parseFloat(item.UnitPriceInclVat || item.UnitPriceInclVAT || 0),
      SerialNumber: item.ProductSerial || item.SerialNumber || '', // ✅ Include serial number for print template
      // Calculate total for each line item
      Total: parseFloat(item.TotalAmountInclVATAfterDiscount || item.TotalAmountInclVAT || item.TotalPrice || (item.Quantity * (item.UnitPriceInclVat || item.UnitPrice)) || 0)
    }));


    // Prepare customer data
    const customer = ticket.Customer ||
      (ticket.CustomerId ? { Id: ticket.CustomerId, Name: ticket.CustomerName || 'Walk-in Customer' } : null);

    // Walk-in name: CustomerName when no linked customer account
    const walkInCustomerName = !ticket.Customer && !ticket.CustomerId
      ? (ticket.CustomerName || '')
      : '';
    
    // Use store info from Redux store, then ticket's company info, then fallback
    const company = storeInfo || ticket.CompanyInfo
    
    // Pass payment method as object with Name property to match template expectations
    // ✅ For SplitPayments, build a combined display name
    let paymentMethod;
    if (ticket.SplitPayments && Array.isArray(ticket.SplitPayments) && ticket.SplitPayments.length > 0) {
      const splitNames = ticket.SplitPayments.map(sp => {
        const pm = paymentMethodsFromStore.find(p => p.Id === sp.PaymentMethodId);
        return `${pm?.Name || sp.PaymentMethodName || 'N/A'} (€${parseFloat(sp.Amount || 0).toFixed(2)})`;
      }).join(', ');
      paymentMethod = { Name: splitNames };
    } else if (ticket.Payments && Array.isArray(ticket.Payments) && ticket.Payments.length > 0) {
      // ✅ Fallback: aggregate from Payments array
      const aggPayments = getAggregatedPayments(ticket.Payments);
      paymentMethod = { Name: aggPayments.map(p => `${p.methodName} (€${parseFloat(p.totalAmount || 0).toFixed(2)})`).join(', ') };
    } else {
      const rawName = ticket.PaymentMethodName || ticket.PaymentMethod || 'N/A';
      // ✅ Replace raw "MULTIPLE" keyword with a readable label
      paymentMethod = { Name: /^multiple$/i.test(rawName) ? (t('multiple_payment_methods') || 'Multiple Payments') : rawName };
    }
    const paidAmount = parseFloat(ticket.PaidAmount || ticket.TotalAmountInclVATAfterDiscount || ticket.TotalAmount || 0);
    const receiptNumber = ticket.ReceiptNumber || ticket.TicketNumber;
    
    // Map discount data if exists
    const discount = ticket.DiscountAmount && ticket.DiscountAmount > 0 ? {
      type: ticket.DiscountType?.toLowerCase() === 'percentage' ? 'percentage' : 'fixed',
      value: ticket.DiscountType?.toLowerCase() === 'percentage' 
        ? parseFloat(ticket.DiscountPercentage || 0) 
        : parseFloat(ticket.DiscountAmount || 0),
      amount: parseFloat(ticket.DiscountAmount || 0),
      reason: ticket.DiscountReason || '',
    } : null;
    
    const customVATRate = ticket.VATPercentage !== null && ticket.VATPercentage !== undefined 
      ? parseFloat(ticket.VATPercentage) 
      : null;


    // Validate cart data
    if (!cart || cart.length === 0) {
      console.warn('⚠️ No line items found in ticket');
      toast.warning(t('no_items_in_ticket') || 'No items found in this ticket');
      return;
    }

    // ✅ Map backend TicketType to display format (handles both string and integer)
    const normalizedType = normalizeTicketType(ticket.TicketType);
    const ticketType = normalizedType === TICKET_TYPE.REPAIR ? 'Repair' : 'Sale';
    const pickupDate = ticket.PickupDate || null;
    const notes = ticket.Notes || undefined;

    // Generate receipt HTML
    const receiptHtml = getThermalReceiptTemplate(
      cart,
      customer,
      company,
      paymentMethod,
      paidAmount,
      receiptNumber,
      discount,
      customVATRate,
      notes, // ✅ Use actual notes from ticket
      undefined, // useMultiplePayments
      undefined, // paymentMethodsArray
      undefined, // paymentMethods lookup
      walkInCustomerName, // ✅ walk-in name for display
      ticketType, // ✅ Pass ticket type ('Sale' or 'Repair')
      pickupDate, // ✅ Pass pickup date for repair tickets
      false // isTvaIncluded
    );

    setPreviewHtml(receiptHtml);
    setCurrentTicketForPrint(ticket);
    setPreviewOpen(true);
  };

  const handlePrint = () => {
    try {
      
      // Create a hidden iframe for printing
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'absolute';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = 'none';
      
      document.body.appendChild(printFrame);
      
      const printDocument = printFrame.contentWindow || printFrame.contentDocument;
      if (printDocument.document) {
        printDocument.document.open();
        printDocument.document.write(previewHtml);
        printDocument.document.close();
      }
      
      // Wait for content to load then print
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        
        // Remove iframe after printing
        setTimeout(() => {
          document.body.removeChild(printFrame);
          setPreviewOpen(false);
          toast.success(t('print_sent') || 'Print job sent successfully');
        }, 100);
      }, 250);
    } catch (error) {
      console.error('❌ Print error:', error);
      toast.error(t('print_failed') || 'Failed to print receipt');
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewHtml('');
    setCurrentTicketForPrint(null);
  };

  const getPaymentMethodColor = (method) => {
    const colors = {
      CASH: 'success',
      BANCONTACT: 'primary',
      VISA: 'info',
      MASTERCARD: 'warning',
      CHEQUE: 'secondary',
      VIREMENT: 'default',
      PREPAID: 'info',
    };
    return colors[method?.toUpperCase()] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Completed': 'success',
      'Pending': 'warning',
      'Cancelled': 'error',
      'Refunded': 'info',
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Helper function to get aggregated payment methods from Payments array
  const getAggregatedPayments = React.useCallback((payments) => {
    if (!payments || payments.length === 0) return [];
    
    const paymentMap = {};
    payments.forEach((payment) => {
      const methodName = payment.PaymentMethodName || payment.PaymentMethod?.Name || 'Unknown Method';
      const amount = Number(payment.Amount || 0);
      
      if (paymentMap[methodName]) {
        paymentMap[methodName] += amount;
      } else {
        paymentMap[methodName] = amount;
      }
    });
    
    return Object.entries(paymentMap).map(([methodName, totalAmount]) => ({
      methodName,
      totalAmount
    }));
  }, []);

  // Calculate total amount from line items
  const calculateTicketTotal = (ticket) => {
    if (ticket.TotalAmountInclVAT !== null && ticket.TotalAmountInclVAT !== undefined) {
      return parseFloat(ticket.TotalAmountInclVAT);
    }
    if (ticket.TotalAmount !== null && ticket.TotalAmount !== undefined) {
      return parseFloat(ticket.TotalAmount);
    }
    // Calculate from line items if not available
    const lineItems = ticket.LineItems || ticket.Items || [];
    return lineItems.reduce((sum, item) => {
      const itemTotal = parseFloat(item.TotalPrice || (item.Quantity * item.UnitPrice) || 0);
      return sum + itemTotal;
    }, 0);
  };

  // Get line items count
  const getLineItemsCount = (ticket) => {
    const lineItems = ticket.LineItems || ticket.Items || [];
    return lineItems.length;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('tickets_de_caisse') || 'Tickets de Caisse'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('manage_all_tickets') || 'Manage all your tickets de caisse'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchTickets}
            sx={{ borderRadius: 2 }}
          >
            {t('refresh') || 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateTicket}
            sx={{ borderRadius: 2 }}
          >
            {t('create_ticket') || 'Create Ticket'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          placeholder={t('search_tickets') || 'Search by ticket number, customer name, payment method...'}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ bgcolor: 'white' }}
        />
      </Paper>

      {/* Tickets Table */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('ticket_number') || 'Ticket #'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('no_line_items') || 'No. Line Items'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('date_time') || 'Date & Time'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('customer') || 'Customer'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('ticket_type') || 'Type'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('payment_method') || 'Payment Method'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="right">
                  {t('amount') || 'Amount'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="right">
                  {t('due_amount') || 'Due Amount'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                  {t('status') || 'Status'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }} align="center">
                  {t('actions') || 'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm
                        ? t('no_tickets_found') || 'No tickets found matching your search'
                        : t('no_tickets_yet') || 'No tickets de caisse yet. Create your first ticket!'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((ticket) => (
                    <TableRow
                      key={ticket.Id}
                      sx={{
                        '&:hover': { bgcolor: '#f9f9f9' },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {ticket.TicketNumber || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.LineItemsCount || getLineItemsCount(ticket) || 0}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {formatDate(ticket.CreatedDate || ticket.Date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(ticket.CreatedDate || ticket.Date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ticket.CustomerName || t('walk_in_customer') || 'Walk-in Customer'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getTicketTypeLabel(ticket.TicketType, t)}
                          size="small"
                          color={getTicketTypeColor(ticket.TicketType)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        {ticket.SplitPayments && Array.isArray(ticket.SplitPayments) && ticket.SplitPayments.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {ticket.SplitPayments.map((sp, idx) => {
                              // ✅ Map PaymentMethodId to actual payment method name
                              const paymentMethod = paymentMethodsFromStore.find(pm => pm.Id === sp.PaymentMethodId);
                              const methodName = paymentMethod?.Name || sp.PaymentMethodName || sp.PaymentMethod || 'N/A';
                              return (
                                <Chip
                                  key={idx}
                                  label={methodName}
                                  size="small"
                                  color={getPaymentMethodColor(methodName)}
                                  sx={{ fontWeight: 600 }}
                                />
                              );
                            })}
                          </Box>
                        ) : ticket.Payments && Array.isArray(ticket.Payments) && ticket.Payments.length > 0 ? (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {getAggregatedPayments(ticket.Payments).map((payment, idx) => (
                              <Chip
                                key={idx}
                                label={payment.methodName}
                                size="small"
                                color={getPaymentMethodColor(payment.methodName)}
                                sx={{ fontWeight: 600 }}
                              />
                            ))}
                          </Box>
                        ) : (
                          <Chip
                            label={
                              /^multiple$/i.test(ticket.PaymentMethodName || ticket.PaymentMethod || '')
                                ? (t('multiple_payment_methods') || 'Multiple Payments')
                                : (ticket.PaymentMethodName || ticket.PaymentMethod || 'N/A')
                            }
                            size="small"
                            color={/^multiple$/i.test(ticket.PaymentMethodName || ticket.PaymentMethod || '') ? 'secondary' : getPaymentMethodColor(ticket.PaymentMethodName || ticket.PaymentMethod)}
                            sx={{ fontWeight: 600 }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                          {formatCurrency(calculateTicketTotal(ticket))}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {(ticket.DueAmount || 0) > 0 ? (
                          <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                            {formatCurrency(ticket.DueAmount || 0)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={ticket.Status || 'Completed'}
                          size="small"
                          color={getStatusColor(ticket.Status)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('view') || 'View'}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewTicket(ticket)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('edit') || 'Edit'}>
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleEditTicket(ticket.Id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredTickets.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>
    </Container>
  );
};

export default TicketDeCaissePage;